import { nodeConfig } from '@scripts/mixins';
import { setHtmlStateLock, toggleHtmlClass } from '@scripts/helpers/utils';
import deeplink, { updateHash } from '@scripts/helpers/deeplink';

export const NAMESPACE = 'modal';
export const DEFAULT_SELECTOR = '[data-js-modal]';
const CLOSE_SELECTOR = '[data-js-modal-close]';

/**
 * @typedef {Object} ModalSettings
 * @property {boolean} [scrollLock=true] - Lock body scroll when open.
 * @property {boolean} [closeOnBackdropClick=false] - Close when clicking outside dialog content.
 * @property {boolean} [deeplink=false] - Enable URL hash updates.
 * @property {string|null} [htmlStateClass=null] - Class added to <html> when open. Auto-generates "state-{id}-open" if null.
 */
export const DEFAULT_SETTINGS = {
	scrollLock: true,
	closeOnBackdropClick: false,
	deeplink: false,
	htmlStateClass: null
};

/**
 * Native dialog modal wrapper.
 * @fires modal:open
 * @fires modal:close
 */
export class Modal {
	/**
	 * @param {HTMLDialogElement} node
	 * @param {ModalSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;

		if (this.config.htmlStateClass === null && this.elNode.id) {
			this.config.htmlStateClass = `state-${this.elNode.id}-open`;
		}
	}

	init() {
		this._setup();
		this._bindUI();

		if (this.config.deeplink) {
			this._setupDeepLinks();
		}
	}

	/** @returns {boolean} */
	isOpen() {
		return this.elNode.open;
	}

	open() {
		if (this.isOpen()) return;

		if (this.config.scrollLock) {
			setHtmlStateLock(true);
		}

		this.elNode.showModal();

		toggleHtmlClass(this.config.htmlStateClass, true);

		if (this.config.deeplink) updateHash(this.elNode.id, true);

		this._dispatch('open');
	}

	close() {
		if (!this.isOpen()) return;

		this.elNode.close();
	}

	_setup() {
		if (!this.elNode.hasAttribute('aria-modal')) {
			this.elNode.setAttribute('aria-modal', 'true');
		}

		if (!this.elNode.id) return;

		const triggers = document.querySelectorAll(`[data-js-modal-trigger="${this.elNode.id}"]`);

		triggers.forEach((trigger) => {
			if (!trigger.hasAttribute('aria-haspopup')) {
				trigger.setAttribute('aria-haspopup', 'dialog');
			}
		});
	}

	_setupDeepLinks() {
		if (!this.elNode.id) return;

		deeplink.register(`${DEFAULT_SELECTOR}#${this.elNode.id}`, () => {
			if (this.isOpen()) return;

			this.open();
			this.elNode.focus();
		});
	}

	/** @param {string} action */
	_dispatch(action) {
		this.elNode.dispatchEvent(
			new CustomEvent(`${NAMESPACE}:${action}`, {
				bubbles: true,
				detail: { instance: this }
			})
		);
	}

	_bindUI() {
		this._onClose = this._onClose.bind(this);
		this._onBackdropClick = this._onBackdropClick.bind(this);
		this._onTriggerClick = this._onTriggerClick.bind(this);
		this._onCloserClick = this._onCloserClick.bind(this);

		this.elNode.addEventListener('close', this._onClose);
		this.elNode.addEventListener('click', this._onBackdropClick);
		this.elNode.addEventListener('click', this._onCloserClick);

		document.addEventListener('click', this._onTriggerClick);
	}

	_onTriggerClick(event) {
		const trigger = event.target.closest(`[data-js-modal-trigger="${this.elNode.id}"]`);

		if (!trigger) return;

		this.open();
	}

	_onCloserClick(event) {
		if (!event.target.closest(CLOSE_SELECTOR)) return;

		this.close();
	}

	_onClose() {
		if (this.config.scrollLock) {
			setHtmlStateLock(false);
		}

		toggleHtmlClass(this.config.htmlStateClass, false);

		if (this.config.deeplink) updateHash(this.elNode.id, false);

		this._dispatch('close');
	}

	/** Rect check excludes clicks on dialog padding, not just child elements. */
	_onBackdropClick(event) {
		if (!this.config.closeOnBackdropClick) return;
		if (event.target !== this.elNode) return;

		const rect = this.elNode.getBoundingClientRect();
		const isOutside =
			event.clientY < rect.top ||
			event.clientY > rect.bottom ||
			event.clientX < rect.left ||
			event.clientX > rect.right;

		if (!isOutside) return;

		this.close();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Modal(node, config);

		module.init();

		return module;
	});
}
